import i18next from "i18next";
import { Size, Style, TableCell } from "pdfmake/interfaces";
import { useTranslation } from "react-i18next";
import { MONEY_REGEX, getCurrencies, getLang } from "go-core/utils/utils";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ListConfigField } from "../../../services/types";
import { ListConfigColumn } from "../../Column/services/types";
import { ListConfigFilter } from "../../Filter/services/types";
import RenderColumnUtils from "./RenderColumnUtils";
import { ExportConfig } from "./types";

const TranslationsInitialized = () => {
	const { t } = useTranslation();
	t("enums.languages.pl", { ns: "lib" });
	t("enums.languages.en", { ns: "lib" });
	t("enums.languages.de", { ns: "lib" });
	t("enums.languages.uk", { ns: "lib" });
	t("enums.languages.vi", { ns: "lib" });
	t("enums.languages.es", { ns: "lib" });
};

const getCustomFieldExportValue = (columnFromConfigId: string, item: any): string | undefined => {
	if (!(columnFromConfigId.startsWith("+") || columnFromConfigId.startsWith("-"))) return "";
	const key = columnFromConfigId.substring(1);
	const value = item.custom_fields?.find((f: CustomFieldTemplateApi) => f.slug === key)?.value;
	return value ? value : "";
};

const parseValue = (text: string) => {
	const lang = getLang();
	if (lang === "pl" || lang === "pl-PL") {
		return `\"${text.replace(/\./g, ",")}\"`;
	}
	return `\"${text}\"`;
};

export const valueEncoder = (value: string): string => {
	return value.replace(/[#"']/g, "");
};

export const createCSVFile = (data: string, exportConfig: ExportConfig | undefined): void => {
	const language = getLang();
	const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
	const encodedUri = URL.createObjectURL(blob);
	// let encodedUri = encodeURI(csvContent);
	const link = document.createElement("a");
	link.setAttribute("href", encodedUri);
	link.setAttribute(
		"download",
		`${exportConfig?.filename}_${language === "pl-PL" || language === "pl" ? "pl" : "en"}.csv`
	);
	link.style.display = "none";
	document.body.appendChild(link); // Required for FF

	link.click();
};

export const exportCustomPdf = (data: string, exportConfig: ExportConfig | undefined): void => {
	const language = getLang();
	const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf;charset=UTF-8" }));
	const link = document.createElement("a");
	link.setAttribute("href", url);
	link.setAttribute(
		"download",
		`${exportConfig?.filename}_${language === "pl-PL" || language === "pl" ? "pl" : "en"}.pdf`
	);
	document.body.appendChild(link);
	link.click();
};

const parseCsvColumnValue = (filters: ListConfigFilter[], value: any, columnFromConfig?: ListConfigColumn): string => {
	const availableCurrencies = getCurrencies();

	const columnFieldType = columnFromConfig?.exportType || filters.find((f) => f.id === columnFromConfig?.id)?.type;
	let parsedValue = value;
	if (columnFieldType === "number") {
		parsedValue = parseValue(valueEncoder(`"${parsedValue}"`));
	} else {
		if (typeof parsedValue === "string" || parsedValue instanceof String) {
			if (parsedValue.includes('"')) {
				parsedValue = parsedValue.replaceAll('"', '""');
			}
		} else {
			console.log("NOT STRING: ", parsedValue, columnFromConfig);
		}
		parsedValue = `"${parsedValue}"`;
	}
	const currency = availableCurrencies.find((curr) => parsedValue.includes(curr.value));

	if (currency) {
		parsedValue = parsedValue.replace(currency.value, "");
	}

	return parsedValue;
};

export const exportCsv = (
	selectedColumns: Array<string>,
	columnsConfig: ListConfigField[],
	filters: ListConfigFilter[],
	data: any[],
	exportConfig: ExportConfig | undefined,
	nestedItemsPropertyName?: string,
	summaryData?: Record<string, any>,
	withSummary?: boolean
): void => {
	let csvContent = "data:text/csv;charset=UTF-8,";
	const rows: any[] = [];
	const thead: string[] = [];
	const filteredColumns = columnsConfig
		.filter((columnConfig) => selectedColumns.includes(columnConfig.id))
		.flatMap((columnConfig) => columnConfig.exportCustomColumns ?? columnConfig);
	const dataIncludingNestedItems = data.flatMap((dataItem) => {
		if (
			nestedItemsPropertyName &&
			Array.isArray(dataItem[nestedItemsPropertyName]) &&
			dataItem[nestedItemsPropertyName].length > 0
		) {
			return [dataItem, ...dataItem[nestedItemsPropertyName]];
		}

		return dataItem;
	});
	filteredColumns.forEach((column) => {
		thead.push(`"${column?.name}"`);
	});
	rows.push(thead);
	dataIncludingNestedItems.forEach((item) => {
		const bodyRow: string[] = [];
		filteredColumns.forEach((column) => {
			const val = getCellContentToRender(item, column) ?? "";
			const parsedValue = parseCsvColumnValue(filters, val, column);
			bodyRow.push(parsedValue);
		});
		rows.push(bodyRow);
	});

	if (summaryData && withSummary && !exportConfig?.skipCSVSummaryRow) {
		const summaryRow: string[] = [];
		filteredColumns.forEach((column) => {
			const parsedValue = parseCsvColumnValue(filters, column?.renderSummary?.(summaryData) ?? "", column);
			summaryRow.push(parsedValue);
		});
		rows.push(summaryRow);
	}

	// csvContent += rows.map(e => e.join(',')).join("\n");
	csvContent = rows.map((e) => e.join(",")).join("\n");
	csvContent = csvContent.replace(/,\n/g, "\n");

	createCSVFile(csvContent, exportConfig);
};

export const exportPdf = async (
	columns: Array<string>,
	columnsConfig: ListConfigColumn[],
	filterNames: Array<string>,
	data: any[],
	exportConfig: ExportConfig | undefined,
	additionalInformationOnTheTop?: { text: string; fontSize: number }[],
	nestedItemsPropertyName?: string,
	summaryData?: Record<string, any>,
	withSummary?: boolean
): Promise<void> => {
	const table = generatePdfTable(
		columns,
		columnsConfig,
		data,
		exportConfig,
		nestedItemsPropertyName,
		summaryData,
		withSummary
	);
	let organizationName: string | undefined = undefined;
	let companyName: string | undefined = undefined;
	let taxIdNo: string | undefined = undefined;

	if (
		(exportConfig?.company && exportConfig.company !== "undefined") ||
		(exportConfig?.taxIdNo && exportConfig.taxIdNo !== "undefined")
	) {
		if (exportConfig?.company && exportConfig.company !== "undefined") companyName = exportConfig.company;
		if (exportConfig?.taxIdNo && exportConfig.taxIdNo !== "undefined") taxIdNo = exportConfig.taxIdNo;
	} else if (exportConfig?.organization && exportConfig.organization !== "undefined") {
		organizationName = exportConfig?.organization;
	}

	let filterDefinition: Record<string, any> | null = null;
	if (filterNames.length > 0) {
		filterDefinition = {
			text: `${i18next.t("lib:common.word.filters")}:\n ${filterNames.join("\n")}`,
			fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) + 3 : 12,
		};
	}
	const docDefinition: any = {
		pageOrientation: table.pageOrientation,
		pageMargins: [20, 20, 20, 40],
		footer(currentPage: any, pageCount: any) {
			const d = new Date();
			const utc = `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
			let footerFirstColumnText = "";

			if (organizationName) footerFirstColumnText += `${i18next.t("lib:common.word.venue")}: ${organizationName}`;
			if (companyName) footerFirstColumnText += `\n ${companyName}`;
			if (taxIdNo) footerFirstColumnText += `\n ${i18next.t("lib:common.word.tax_id_no.title")}: ${taxIdNo}`;
			footerFirstColumnText += `\n ${utc} by GoPOS.pl`;

			return {
				columns: [
					{
						text: `${footerFirstColumnText}`,
						alignment: "left",
						fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 1 : 8,
						margin: [20, organizationName ? 10 : 0, 0, 0],
					},
					{
						text: `${i18next.t("lib:common.word.page")} ${currentPage.toString()} ${i18next.t(
							"lib:common.word.of"
						)} ${pageCount}`,
						fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 1 : 8,
						alignment: "right",
						margin: [0, 20, 20, 0],
					},
				],
			};
		},
		content: [
			{
				text: `${exportConfig?.title}`,
				bold: true,
				fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) + 6 : 15,
				alignment: "center",
				margin: [0, 0, 0, 20],
			},
			additionalInformationOnTheTop ? additionalInformationOnTheTop.map((information) => information) : {},
			filterDefinition,
			{
				style: "tableExample",
				color: "#444",
				table,
			},
		],
		styles: table.overWidth ? RenderColumnUtils.getPdfStylesSmall() : RenderColumnUtils.getPdfStyles(),
	};

	const pdfMake = (await import("pdfmake/build/pdfmake")).default;
	const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
	pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts;

	pdfMake.createPdf(docDefinition).download(`${exportConfig?.filename}.pdf`);
};

const getTableStyles = (exportConfig?: ExportConfig): Record<string, Style> => ({
	bold: {
		bold: true,
	},
	small: {
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 3 : 6,
		color: "#9b9b9b",
	},
	header: {
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 1 : 8,
		bold: true,
	},
	subheader: {
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 1 : 8,
		bold: true,
		margin: [0, 10, 0, 5],
	},
	tableExample: {
		margin: [0, 10, 0, 0],
	},
	tableHeader: {
		bold: true,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) + 1 : 10,
		color: "#333a38",
	},
	tableSmall: {
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) - 1 : 8,
		color: "#6b6d6d",
	},
	tableCell: {
		bold: false,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) : 9,
		color: "#464e4c",
	},
	tableCellNestedParent: {
		bold: true,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) : 9,
		color: "#464e4c",
		fillColor: "#bcc0c7",
	},
	tableCellNested: {
		bold: false,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) : 9,
		color: "#464e4c",
		fillColor: "#bcc0c7",
	},
	evenDraftCell: {
		bold: false,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) : 9,
		color: "#464e4c",
		fillColor: "#F0F0F0",
	},
	draftCell: {
		bold: false,
		fontSize: exportConfig?.pdfFontSize ? Number(exportConfig.pdfFontSize) : 9,
		color: "#464e4c",
	},
});

const getCellContentToRender = (item: Record<string, any>, columnFromConfig?: ListConfigColumn): string | undefined => {
	if (!columnFromConfig) return undefined;

	const { renderExport, render, id } = columnFromConfig;
	const rawItemValue = item[id];

	if (renderExport || render) {
		const contentToRender = (renderExport ?? render)(item);
		const isMoney = typeof contentToRender === "string" && MONEY_REGEX.test(contentToRender);
		const moneySeparator = navigator.language === "pl" ? "," : ".";

		return isMoney ? contentToRender.replace(/[,.]/, moneySeparator) : contentToRender;
	}

	return getCustomFieldExportValue(id, item) || rawItemValue;
};

const getExportPDFCellStyle = (index: number, exportConfig?: ExportConfig, isParentToNestedElements?: boolean) => {
	const { tableCell, evenDraftCell, tableCellNestedParent } = getTableStyles(exportConfig);

	if (isParentToNestedElements) {
		return tableCellNestedParent;
	} else if (exportConfig?.everyOtherLineInDifferentColor && index % 2 !== 0) {
		return evenDraftCell;
	}

	return tableCell;
};

const generatePdfTable = (
	selectedColumns: Array<string>,
	columnsConfig: ListConfigField[],
	data: any[],
	exportConfig?: ExportConfig,
	nestedItemsPropertyName?: string,
	summaryData?: Record<string, any>,
	withSummary?: boolean
) => {
	const widths: Size[] = [];
	const tHead: any[] = [];
	const A4Height = 842;

	const filteredColumns = columnsConfig
		.filter((columnConfig) => selectedColumns.includes(columnConfig.id))
		.flatMap((columnConfig) => columnConfig.exportCustomColumns ?? columnConfig);

	const getStyles = (column?: ListConfigField) => {
		if (column?.styleOverride) {
			return [
				RenderColumnUtils.parsePdfMoneyStyles(column.styleOverride),
				getTableStyles(exportConfig).tableCell,
			];
		}
		return getTableStyles(exportConfig).tableCell;
	};

	filteredColumns.forEach((columnConfig) => {
		tHead.push({
			text: columnConfig.name,
			style: columnConfig.styleOverride
				? [
						RenderColumnUtils.parsePdfMoneyStyles(columnConfig.styleOverride),
						getTableStyles(exportConfig).tableHeader,
				  ]
				: getTableStyles(exportConfig).tableHeader,
		});
		widths.push("auto");
	});
	const tableBody: TableCell[][] = [];
	const table = {
		widths,
		body: tableBody,
		dontBreakRows: true,
		pageOrientation: exportConfig?.pdfOrientation ?? "landscape",
		overWidth: false,
	};
	table.body.push(tHead);

	const newWidthsArray = table.widths.map(() => 0);
	const defaultMaxWidth = (A4Height * 1.5) / selectedColumns.length;

	if (Array.isArray(data)) {
		const dataIncludingNestedItems = data.flatMap((dataItem) => {
			if (
				nestedItemsPropertyName &&
				Array.isArray(dataItem[nestedItemsPropertyName]) &&
				dataItem[nestedItemsPropertyName].length > 0
			) {
				const nestedItems = dataItem[nestedItemsPropertyName].map((nestedItem: any) => ({
					...nestedItem,
					isNestedItem: true,
				}));
				return [{ ...dataItem, isParentToNestedElements: true }, ...nestedItems];
			}

			return dataItem;
		});

		dataIncludingNestedItems.forEach((item, itemIndex) => {
			const bodyRow: any[] = [];
			const { isParentToNestedElements, isNestedItem } = item;
			filteredColumns.forEach((columnConfig, index) => {
				const style = getExportPDFCellStyle(itemIndex, exportConfig, isParentToNestedElements);
				const marginLeft = isNestedItem && index === 0 ? 20 : 0;
				const text = getCellContentToRender(item, columnConfig);

				bodyRow.push({ text: { text, noWrap: false }, style, marginLeft });

				if (columnConfig.exportColumnFixedWidth) {
					newWidthsArray[index] = columnConfig.exportColumnFixedWidth;
				} else {
					const maxWidth = columnConfig.exportColumnWidth || defaultMaxWidth;
					const row = bodyRow[index];
					if (row) {
						const headLength = tHead[index]?.text.length;
						const contentLength = !row.text.text ? headLength : row.text.text.toString().length;
						const text = (headLength >= contentLength ? tHead[index]?.text : row.text.text) || "";
						if (text) {
							const width = estimatePdfTextWidth(text, headLength >= contentLength, exportConfig);
							if (width > maxWidth) {
								newWidthsArray[index] = maxWidth;
							} else {
								newWidthsArray[index] = Math.max(newWidthsArray[index], width);
							}
						}
					}
				}
			});
			table.body.push(bodyRow);
		});
	}

	if (summaryData && withSummary) {
		const summaryRow: any[] = [];
		filteredColumns.forEach((columnConfig, index) => {
			const text = columnConfig.renderSummary?.(summaryData).toString() ?? "";
			summaryRow.push({
				text: { text, noWrap: false },
				style: getStyles(columnConfig),
			});

			const maxWidth = columnConfig.exportColumnWidth || defaultMaxWidth;
			const headLength = tHead[index]?.text.length;
			const contentLength = !text ? headLength : text.length;
			const newText = (headLength >= contentLength ? tHead[index]?.text : text) || "";
			if (newText) {
				const width = estimatePdfTextWidth(newText, headLength >= contentLength, exportConfig);
				if (width > maxWidth) {
					newWidthsArray[index] = maxWidth;
				} else {
					newWidthsArray[index] = Math.max(newWidthsArray[index], width);
				}
			}
		});
		table.body.push(summaryRow);
	}

	table.widths = newWidthsArray.map((width, index) => {
		if (!width) return table.widths[index];
		return width;
	});

	const tableWidthSum: number = table.widths
		.map((width) => {
			return Number(width) || 0;
		})
		.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

	const fullTableWidth = tableWidthSum + 20 * 2 + 7 * filteredColumns.length;

	// table.pageOrientation = fullTableWidth <= A4Width ? "portrait" : table.pageOrientation;
	if (fullTableWidth > A4Height) {
		table.widths = table.widths.map((width, index) => {
			return (table.widths[index] as number) * 0.85;
		});
		table.overWidth = true;
	} else {
		table.overWidth = false;
	}
	return table;
};

export function estimatePdfTextWidth(text: string | number, isHeader: boolean, exportConfig?: ExportConfig) {
	const lines = text.toString().split("\n");

	const font = "Noto Sans";
	let fontSize: number;
	if (exportConfig?.pdfFontSize) {
		fontSize = isHeader ? Number(exportConfig.pdfFontSize) + 1 : Number(exportConfig.pdfFontSize);
	} else {
		fontSize = isHeader ? 11 : 10;
	}

	const canvas = document.createElement("canvas");
	const context = canvas.getContext("2d")!;
	context.font = `${fontSize}px ${font}`;
	let maxWidth = 0;

	for (const line of lines) {
		const lineWidth = context.measureText(line).width;
		if (lineWidth > maxWidth) {
			maxWidth = lineWidth;
		}
	}
	const extraPadding = fontSize * 0.2;
	return maxWidth + extraPadding;
}
